import { sumWithDecimal, Decimal } from "@syla/shared/decimal";
import { getAssetShortName } from "@syla/shared/helpers/assets/getAssetShortName";
import { isNotNullable } from "@syla/shared/types/helpers/withoutNullable";
import { LedgerType, isTransfer } from "@syla/shared/types/models/LedgerBase";
import { isCgtTaxOutcomeType } from "@syla/shared/types/models/TaxOutcomeBase";
import { NeedsReviewType } from "@syla/shared/types/models/TransactionBase";
import {
  TransactionEvent,
  Ledger,
} from "@syla/shared/types/responses/TransactionEvent";
import { sortBy, uniq } from "lodash";
import React from "react";
import { MarkdocContent } from "../../../../../markdoc/MarkdocContent";

export function renderTransactionNeedsReviewItems({
  transactions,
}: {
  transactions: TransactionEvent[];
}) {
  const ledgers = transactions.flatMap((t) => t.ledgers);

  const uniqueReviewTypes = sortBy(
    uniq(
      transactions.flatMap((t) => t.needsReview?.map((nr) => nr.type) ?? []),
    ),
  );

  return uniqueReviewTypes.flatMap((nrType) =>
    renderNeedsReviewItems(ledgers, nrType),
  );
}

function renderNeedsReviewItems(
  ledgers: Ledger[],
  needsReviewType: NeedsReviewType,
): JSX.Element[] {
  const messages = parseReviewItemMessages(ledgers, needsReviewType);

  return messages.map((message, index) => (
    <MarkdocContent content={message} key={index}>
      {message}
    </MarkdocContent>
  ));
}

function formatLedgerAmount(amount: Decimal) {
  return amount.format({
    separateThousands: true,
  }).text;
}

function parseReviewItemMessages(
  ledgers: Ledger[],
  needsReviewType: NeedsReviewType,
): string[] {
  switch (needsReviewType) {
    case NeedsReviewType.UnmatchedTransfer:
      return parseUnmatchedTransferReviewItem(ledgers);
    case NeedsReviewType.IncompleteRebase:
      return parseIncompleteRebaseReviewItem(ledgers);
    case NeedsReviewType.MissingAcquisitionParcel:
      return parseMissingAcquisitionParcelReviewItem(ledgers);
    case NeedsReviewType.MissingMarketValue:
      return parseMissingMarketValueReviewItem(ledgers);
    case NeedsReviewType.MissingAssetCode:
      return parseMissingAssetCodeReviewItem(ledgers);
    case NeedsReviewType.UnclassifiedLedgers:
      return parseUnclassifiedLedgersReviewItem(ledgers);
    // disable these for now since user can do nothing about them
    case NeedsReviewType.WithdrawalInclusiveOfFee:
    case NeedsReviewType.MissingData:
    default:
      return [];
  }
}

function parseUnmatchedTransferReviewItem(ledgers: Ledger[]) {
  return ledgers
    .filter(isTransfer)
    .map(
      (ledger) =>
        `Add a matching **${
          ledger.type === LedgerType.TransferIn ?
            LedgerType.TransferOut
          : LedgerType.TransferIn
        }** of **${formatLedgerAmount(
          ledger.amount.negated(),
        )} ${getAssetShortName(ledger.asset)}** in another transaction.`,
    );
}

function parseIncompleteRebaseReviewItem(ledgers: Ledger[]) {
  return ledgers
    .filter((l) => [LedgerType.RebaseIn, LedgerType.RebaseOut].includes(l.type))
    .map(
      (ledger) =>
        `The **${ledger.type}** must have a matching **${
          ledger.type === LedgerType.RebaseIn ?
            LedgerType.RebaseOut
          : LedgerType.RebaseIn
        }** in this transaction.`,
    );
}

function parseMissingAcquisitionParcelReviewItem(ledgers: Ledger[]) {
  const unmatchedLedgers = ledgers
    .map((ledger) => {
      const unmatchedOutcomes =
        ledger.taxOutcomes?.filter(
          // no sourceLedger means an unmatched acquisition
          (taxOutcome) =>
            isCgtTaxOutcomeType(taxOutcome) && !taxOutcome.sourceLedger,
        ) ?? [];

      const unmatchedAmount = sumWithDecimal(unmatchedOutcomes, "amount");

      if (unmatchedOutcomes.length > 0) {
        return {
          ledger,
          unmatchedAmount,
          unmatchedValue: sumWithDecimal(unmatchedOutcomes, "proceeds"),
        };
      }

      // A RebaseOut has no tax outcome, so we check for a negative
      // balance instead. Since there are no details on the unmatched
      // amount, we can't display it.

      // It would be neater to attach additional details to the
      // NeedsReviewReason when the tax outcomes are processed,
      // instead of inferring like this.
      if (
        ledger.type === LedgerType.RebaseOut &&
        ledger.balances?.balance.isNonZeroNegative()
      ) {
        return {
          ledger,
          unmatchedAmount: undefined,
          unmatchedValue: undefined,
        };
      }

      return undefined;
    })
    .filter(isNotNullable);

  return unmatchedLedgers.map((unmatched) => {
    const ledgerAsset = getAssetShortName(unmatched.ledger.asset);

    if (unmatched?.unmatchedAmount && unmatched?.unmatchedValue) {
      const unmatchedAmount = formatLedgerAmount(unmatched.unmatchedAmount);

      const marketValue = unmatched.ledger.marketValue;
      const unmatchedValue = unmatched.unmatchedValue.format({
        fixedPointDigits: 2,
        currencyCode: "AUD",
        separateThousands: true,
      }).text;

      return (
        `An acquisition of **${unmatchedAmount} ${ledgerAsset}**` +
        (marketValue ? ` (valued at **${unmatchedValue} AUD**)` : "") +
        ` is missing from an earlier transaction.`
      );
    } else {
      return `An acquisition of **${ledgerAsset}** is missing from an earlier transaction.`;
    }
  });
}

function parseMissingMarketValueReviewItem(ledgers: Ledger[]) {
  return ledgers
    .filter((l) => !l.marketValue)
    .map((l) => `Add a market value for the **${l.type}**.`);
}

function parseMissingAssetCodeReviewItem(ledgers: Ledger[]) {
  return ledgers
    .filter((l) => getAssetShortName(l.asset) === "<unknown>")
    .map((l) => `Asset is missing. Change **${l.type}** to the correct asset.`);
}

function parseUnclassifiedLedgersReviewItem(ledgers: Ledger[]) {
  return ledgers
    .filter((l) => [LedgerType.Deposit, LedgerType.Withdrawal].includes(l.type))
    .map((l) => `The **${l.type}** needs a more specific classification.`);
}
